import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  FilterToolbar,
  useNotifications,
  type GridQuery
} from '@ijoin/components';
import {
  GetManagedAccountMethodologiesParams,
  GetManagedAccountMethodologyRowRes,
  useDeleteManagedAccountMethodologyById,
  useGetManagedAccountMethodologies
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { LinkButton } from '../../components';
import { MethodologyCreateDialog } from '../../components/MethodologyCreateDialog';
import { MethodologyFormDialog } from '../../components/MethodologyFormDialog';
import { errorMessage } from '../../helpers';

const MethodologiesPage = () => {
  const notifications = useNotifications();

  const [isCreateOpen, toggleCreateOpen] = useToggle();
  const [pendingDeleteId, setPendingDeleteId] = useState<string>();
  const [editingMethodology, setEditingMethodology] =
    useState<GetManagedAccountMethodologyRowRes>();
  const [query, setQuery] = useState<GridQuery>();

  const methodologies = useGetManagedAccountMethodologies(
    query as GetManagedAccountMethodologiesParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const deleteMethodology = useDeleteManagedAccountMethodologyById({
    mutation: {
      onError: err =>
        notifications.error(errorMessage(err, 'Failed to delete methodology')),
      onSuccess: () => {
        notifications.success('Methodology deleted');
        setPendingDeleteId(undefined);
        methodologies.refetch();
      }
    }
  });

  const onCancelDelete = () => setPendingDeleteId(undefined);

  const onConfirmDelete = () => {
    if (!pendingDeleteId) return;

    deleteMethodology.mutate({
      managedAccountMethodologyId: pendingDeleteId
    });
  };

  const onCloseEdit = () => setEditingMethodology(undefined);

  const onSaved = () => {
    setEditingMethodology(undefined);
    methodologies.refetch();
  };

  const onCreated = () => {
    toggleCreateOpen();
    methodologies.refetch();
  };

  const pendingDeleteRow = methodologies.data?.data.find(
    row => row.managedAccountMethodologyId === pendingDeleteId
  );

  const columns: GridColDef<GetManagedAccountMethodologyRowRes>[] = [
    {
      field: 'managedAccountMethodologyName',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.managedAccountMethodologyName)}
          params={{
            managedAccountMethodologyId: params.row.managedAccountMethodologyId
          }}
          to='/methodologies/$managedAccountMethodologyId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      align: 'right',
      field: 'providersCount',
      headerAlign: 'right',
      headerName: 'Providers',
      sortable: false,
      width: 120
    },
    {
      align: 'right',
      field: 'advisoryOrgsCount',
      headerAlign: 'right',
      headerName: 'Advisory Orgs',
      sortable: false,
      width: 140
    },
    {
      align: 'center',
      display: 'flex',
      field: 'actions',
      headerAlign: 'center',
      headerName: '',
      renderCell: params => (
        <ActionMenu
          actions={[
            {
              content:
                params.row.providersCount > 1
                  ? 'Delete (in use by providers)'
                  : params.row.plansCount > 0
                    ? 'Delete (in use by plans)'
                    : 'Delete',
              destructive: true,
              disabled:
                params.row.providersCount > 1 || params.row.plansCount > 0,
              onSelect: () =>
                setPendingDeleteId(params.row.managedAccountMethodologyId)
            },
            {
              content: 'Edit',
              onSelect: () => setEditingMethodology(params.row)
            }
          ]}
          trigger={
            <IconButton
              data-testid={`actions-${camelCase(params.row.managedAccountMethodologyName)}`}>
              <MoreVertIcon />
            </IconButton>
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Methodologies</Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={[
          {
            label: 'Name',
            name: 'managedAccountMethodologyName',
            type: 'text'
          }
        ]}
        getRowId={row => row.managedAccountMethodologyId}
        initialSort={[{ field: 'managedAccountMethodologyName', sort: 'asc' }]}
        loading={!query || methodologies.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{ right: ['actions'] }}
        rowCount={methodologies.data?.pagination?.total ?? 0}
        rows={methodologies.data?.data ?? []}
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'methodologies'
          },
          toolbar: {
            addLabel: 'Methodology',
            addTestId: 'createMethodology',
            onAdd: toggleCreateOpen,
            onRefresh: methodologies.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'methodologies-FilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      {isCreateOpen && (
        <MethodologyCreateDialog
          isOpen
          onClose={toggleCreateOpen}
          onSuccess={onCreated}
        />
      )}

      {pendingDeleteRow && (
        <ConfirmationDialog
          confirmationText={pendingDeleteRow.managedAccountMethodologyName}
          confirmColor='error'
          confirmText='Delete'
          data-testid='deleteMethodology'
          isOpen
          message={
            <Stack spacing={2}>
              <Typography>
                {`Permanently delete "${pendingDeleteRow.managedAccountMethodologyName}". This cannot be undone.`}
              </Typography>
              <Typography color='error'>
                {pendingDeleteRow.providersCount
                  ? 'This will also delete its managed account provider, that provider’s portfolios, and its AUM report history and advisory organization links.'
                  : 'This will also delete its AUM report history and advisory organization links.'}
              </Typography>
            </Stack>
          }
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
          title='Delete methodology'
        />
      )}

      {editingMethodology && (
        <MethodologyFormDialog
          isOpen
          methodology={editingMethodology}
          onClose={onCloseEdit}
          onSuccess={onSaved}
        />
      )}
    </Stack>
  );
};

export const Route = createFileRoute('/methodologies/')({
  component: MethodologiesPage
});
